/* eslint-disable indent */
/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';
import { Grid, makeStyles, Tooltip, Typography } from '@material-ui/core';
import VerifiedUserTwoToneIcon from '@material-ui/icons/VerifiedUserTwoTone';
import SecurityTwoToneIcon from '@material-ui/icons/SecurityTwoTone';
import WarningTwoToneIcon from '@material-ui/icons/WarningTwoTone';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import i18n from 'i18n';
import {
  EnvironmentBehaviorEnum,
  EnvironmentFields,
  InstanceFields,
  ZeroTrustStateProps,
} from 'modules/environments/interfaces';
import {
  cyrb53Hash,
  dateFormatWithAt,
  formatDateWithMoment,
  valueToDaysMinutesSeconds,
} from 'utils/helpers';
import clsx from 'clsx';
import { ModalFields } from 'modules/modal/interfaces';
import { PermissionsData, RolesEnum } from 'modules/authenticate/interfaces';
import { isClientOperator } from 'utils/role';
import { differenceInSeconds, parseISO } from 'date-fns';
import { formatNumberBenchmark } from 'utils/tsHelpers';
import styles from './styles';
import AlarmsAndLogs from '../AlarmsAndLogs';
import InstanceSessions from './InstanceSessions';

const useStyles = makeStyles(styles);

export const ZeroTrustIcon = ({ status }: ZeroTrustStateProps) => {
  const classes = useStyles();
  const map = {
    enabled: (
      <Tooltip
        arrow
        title={i18n.t('ZERO_TRUST_ENABLED_KEY')}
        className={classes.tooltip}
      >
        <VerifiedUserTwoToneIcon className={classes.enabledZeroTrust} />
      </Tooltip>
    ),
    grace_period: (
      <Tooltip
        arrow
        title={i18n.t('ZERO_TRUST_GRACE_PERIOD_KEY')}
        className={classes.tooltip}
      >
        <SecurityTwoToneIcon className={classes.startingZeroTrust} />
      </Tooltip>
    ),
    failed: (
      <Tooltip
        arrow
        title={i18n.t('ZERO_TRUST_FAILED_KEY')}
        className={classes.tooltip}
      >
        <SecurityTwoToneIcon className={classes.failedZeroTrust} />
      </Tooltip>
    ),
    starting: (
      <Tooltip
        arrow
        title={i18n.t('ZERO_TRUST_STARTING_KEY')}
        className={classes.tooltip}
      >
        <SecurityTwoToneIcon className={classes.startingZeroTrust} />
      </Tooltip>
    ),
    disabled: (
      <Tooltip
        arrow
        title={i18n.t('ZERO_TRUST_DISABLED_KEY')}
        className={classes.tooltip}
      >
        <SecurityTwoToneIcon className={classes.disabledZeroTrust} />
      </Tooltip>
    ),
  };

  if (status === null) {
    return (
      <Tooltip
        arrow
        title={i18n.t('ZERO_TRUST_WRONG_VERSION_KEY')}
        className={classes.tooltip}
      >
        <WarningTwoToneIcon className={classes.failedZeroTrust} />
      </Tooltip>
    );
  }

  return map[status] || null;
};

export const getBenchmarkLabel = (value: number): string => {
  if (value <= 7000) {
    return i18n.t('LOW_PERFORMANCE_KEY');
  }
  if (value <= 11000) {
    return i18n.t('MODERATE_PERFORMANCE_KEY');
  }
  if (value <= 12500) {
    return i18n.t('MODERATE_TO_HIGH_PERFORMANCE_KEY');
  }
  return i18n.t('HIGH_PERFORMANCE_KEY');
};

interface BenchmarkIconProps {
  value: number | null;
  state: 'enabled' | 'disabled' | 'platform_old_version';
  large?: boolean;
  finishedAt: string | null;
  executionIntervalMinutes: number | null;
  durationSeconds: number | null;
}

export const BenchmarkIcon = ({
  value,
  state,
  large = false,
  finishedAt,
  executionIntervalMinutes,
  durationSeconds,
}: BenchmarkIconProps) => {
  const classes = useStyles();

  if (state === 'disabled') {
    return null;
  }

  const noBenchmarkAvailable = state === 'enabled' && value === null;

  const platformOldVersion = state === 'platform_old_version';

  const iconStyle = {
    height: large ? 22 : 16,
    width: large ? 22 : 16,
  };

  if (noBenchmarkAvailable) {
    return (
      <Tooltip
        arrow
        title={i18n.t('BENCHMARK_NULL_KEY')}
        className={classes.tooltip}
      >
        <TrendingUpIcon className={classes.benchmarkNull} style={iconStyle} />
      </Tooltip>
    );
  }

  if (platformOldVersion) {
    return (
      <Tooltip
        arrow
        title={i18n.t('PLATFORM_OLD_VERSION_KEY')}
        className={classes.tooltip}
      >
        <TrendingUpIcon className={classes.benchmarkNull} style={iconStyle} />
      </Tooltip>
    );
  }

  const formattedValue = formatNumberBenchmark(value);

  let colorClass = '';
  let tooltipText = '';

  if (value !== null) {
    if (value <= 7000) {
      colorClass = classes.benchmarkLow;
      tooltipText = `${i18n.t(
        'LOW_PERFORMANCE_KEY'
      )}: ${formattedValue} (${i18n.t('OPERATIONS_KEY')})`;
    } else if (value <= 11000) {
      colorClass = classes.benchmarkModerate;
      tooltipText = `${i18n.t(
        'MODERATE_PERFORMANCE_KEY'
      )}: ${formattedValue} (${i18n.t('OPERATIONS_KEY')})`;
    } else if (value <= 12500) {
      colorClass = classes.benchmarkModerateToHigh;
      tooltipText = `${i18n.t(
        'MODERATE_TO_HIGH_PERFORMANCE_KEY'
      )}: ${formattedValue} (${i18n.t('OPERATIONS_KEY')})`;
    } else {
      colorClass = classes.benchmarkHigh;
      tooltipText = `${i18n.t(
        'HIGH_PERFORMANCE_KEY'
      )}: ${formattedValue} (${i18n.t('OPERATIONS_KEY')})`;
    }
  }

  return (
    <Tooltip
      arrow
      title={
        <Grid>
          <Grid container>
            <Typography className={classes.dateTimeTooltip}>
              {i18n.t('BENCHMARK_CONFIG_KEY')}
            </Typography>
          </Grid>
          <Grid style={{ marginBottom: 4 }}>
            <Typography className={classes.dateTimeTooltip}>
              {tooltipText}
            </Typography>
          </Grid>
          <Grid style={{ marginBottom: 12 }} justify="center">
            <Typography className={classes.dateTimeTooltip}>
              {'_________________________________________'}
            </Typography>
          </Grid>
          <Grid container>
            <Typography className={classes.dateTimeTooltip}>
              {i18n.t('CONFIG_KEY')}
            </Typography>
          </Grid>
          {executionIntervalMinutes && (
            <Grid style={{ marginBottom: 4 }}>
              <Typography className={classes.dateTimeTooltip}>
                {i18n.t('INTERVAL_IN_MINUTES_KEY', {
                  interval: executionIntervalMinutes,
                })}
              </Typography>
            </Grid>
          )}
          {durationSeconds && (
            <Grid style={{ marginBottom: 4 }}>
              <Typography className={classes.dateTimeTooltip}>
                {i18n.t('PROCESS_TIME_KEY', {
                  seconds: durationSeconds,
                })}
              </Typography>
            </Grid>
          )}
          {finishedAt && (
            <Grid style={{ marginBottom: 4 }}>
              <Typography className={classes.dateTimeTooltip}>
                {i18n.t('BENCHMARK_DATE_AT_KEY', {
                  date: formatDateWithMoment(finishedAt),
                })}
              </Typography>
            </Grid>
          )}
          <Grid style={{ marginBottom: 12 }} justify="center">
            <Typography className={classes.dateTimeTooltip}>
              {'_________________________________________'}
            </Typography>
          </Grid>
          <Grid className={classes.divValue}>
            <Typography className={classes.dateTimeTooltip}>
              {i18n.t('MATRIX_KEY')}
            </Typography>
          </Grid>
          <Grid className={classes.divValue}>
            <Typography className={classes.dateTimeTooltip}>
              {i18n.t('LOW_PERFORMANCE_MATRIX_KEY')}
            </Typography>
          </Grid>
          <Grid className={classes.divValue}>
            <Typography className={classes.dateTimeTooltip}>
              {i18n.t('MODERATE_PERFORMANCE_MATRIX_KEY')}
            </Typography>
          </Grid>
          <Grid className={classes.divValue}>
            <Typography className={classes.dateTimeTooltip}>
              {i18n.t('MODERATE_TO_HIGH_PERFORMANCE_MATRIX_KEY')}
            </Typography>
          </Grid>
          <Grid className={classes.divValue}>
            <Typography className={classes.dateTimeTooltip}>
              {i18n.t('HIGH_PERFORMANCE_MATRIX_KEY')}
            </Typography>
          </Grid>
        </Grid>
      }
      className={classes.tooltip}
    >
      <TrendingUpIcon className={colorClass} style={iconStyle} />
    </Tooltip>
  );
};

export const InstanceDetailsTooltip = ({
  instance,
  classes,
}: {
  instance: InstanceFields;
  classes: Record<string, string>;
}) => {
  const [operationalTime, setOperationalTime] = React.useState('-');

  React.useEffect(() => {
    const updateOperationalTime = () => {
      if (!instance?.status_details?.operational_datetime) {
        setOperationalTime('-');
        return;
      }

      setOperationalTime(
        valueToDaysMinutesSeconds(
          differenceInSeconds(
            new Date(),
            parseISO(instance?.status_details?.operational_datetime)
          )
        )
      );
    };

    updateOperationalTime();

    const interval = setInterval(updateOperationalTime, 5000);

    return () => clearInterval(interval);
  }, [instance]);

  return (
    <>
      <Grid className={classes.divValue}>
        <Typography className={classes.dateTimeTooltip}>
          {i18n.t('DATE_TIME_START_KEY')}
          {':'}
        </Typography>
        <Typography className={classes.dateTimeTooltip}>
          {instance?.status_details?.start_datetime
            ? dateFormatWithAt(instance?.status_details?.start_datetime)
            : '-'}
        </Typography>
      </Grid>

      <Grid className={classes.divValue}>
        <Typography className={classes.dateTimeTooltip}>
          {i18n.t('DATE_TIME_OPERATIONAL_KEY')}
          {':'}
        </Typography>
        <Typography className={classes.dateTimeTooltip}>
          {instance?.status_details?.operational_datetime
            ? dateFormatWithAt(instance?.status_details?.operational_datetime)
            : '-'}
        </Typography>
      </Grid>

      <Grid className={classes.divValue}>
        <Typography className={classes.dateTimeTooltip}>
          {i18n.t('TIME_TO_START_OPERATIONAL_KEY')}
          {':'}
        </Typography>
        <Typography className={classes.dateTimeTooltip}>
          {instance?.status_details?.operational_latency_seconds
            ? valueToDaysMinutesSeconds(
                instance?.status_details?.operational_latency_seconds
              )
            : '-'}
        </Typography>
      </Grid>
      <Grid className={classes.divValue}>
        <Typography className={classes.dateTimeTooltip}>
          {i18n.t('TIME_OPERATIONAL_KEY')}
          {':'}
        </Typography>
        <Typography className={classes.dateTimeTooltip}>
          {operationalTime}
        </Typography>
      </Grid>
      <Grid className={classes.divValueHeight}>
        <Typography className={classes.detailsTooltip}>
          {i18n.t('CLICK_HERE_TO_DETAILS_KEY')}
        </Typography>
      </Grid>
    </>
  );
};

export const getMonitorCellStyle = (
  item: InstanceFields,
  key: keyof InstanceFields,
  classes: Record<string, string>,
  comparisonValue = 100
) => {
  const mediumValue = Math.floor(0.5 * comparisonValue);

  const metricNumber = Number(item[key]);

  return clsx(classes.metrics, {
    [classes.green]: metricNumber < mediumValue,
    [classes.yellow]: metricNumber >= mediumValue,
    [classes.amber]: metricNumber >= Math.floor(0.8 * comparisonValue),
  });
};

export const environmentStatus = (
  component: EnvironmentBehaviorEnum | string
): string => {
  const options = {
    [EnvironmentBehaviorEnum.manual]: i18n.t('ADVISOR_PAGE.MANUAL_KEY'),
    [EnvironmentBehaviorEnum.micro]: i18n.t('ADVISOR_PAGE.MICRO_KEY'),
    [EnvironmentBehaviorEnum.period]: i18n.t('ADVISOR_PAGE.PERIOD_KEY'),
    [EnvironmentBehaviorEnum.spot_period]: i18n.t(
      'ADVISOR_PAGE.SPOT_PERIOD_KEY'
    ),
  };

  return options[component] || component;
};

export const getInitializingInstancesMessage = (
  environmentSelected: EnvironmentFields | null
) => {
  const numInstances =
    environmentSelected?.scale_instances.num_instances_being_created;
  const isPlural = numInstances && numInstances > 1;
  const translationKey = isPlural
    ? 'INIT_INSTANCE_MSG_PLURAL_KEY'
    : 'INIT_INSTANCE_MSG_SINGULAR_KEY';
  return i18n.t(translationKey, { numInstances });
};

export const isRefreshStatusReboot = (
  status: boolean,
  instanceId: string,
  instanceToChangeStatus: string
) => {
  return status === true && instanceId === instanceToChangeStatus;
};

export const getMenuActionItems = (
  item: InstanceFields,
  id: string,
  environmentSelected: EnvironmentFields | null,
  isPeriodOrManual: boolean,
  isMulticloud: boolean,
  canUseZeroTrust: boolean,
  modalClose: () => void,
  modalOpen: (props: ModalFields) => void,
  handleRebootInstance: (environmentId: string, item: InstanceFields) => void,
  handleUnlinkInstance: (uuidInstance: string, instanceId: string) => void,
  handleUnblockInstance: (instanceUuid: string, instanceId: string) => void,
  handleBlockInstance: (instanceUuid: string, name: string) => void,
  handleTestZeroTrust: (item: InstanceFields) => void,
  handleTerminateInstance: (item: InstanceFields) => void,
  handleSimulateApp: (item: InstanceFields) => void,
  handleConnect: (instanceId: string, username: string) => void,
  permissions?: PermissionsData,
  role?: RolesEnum
): any => {
  const menuActionItems: any = [
    {
      key: 'btn-sessions',
      label: i18n.t('SESSIONS_KEY'),
      onClick: () =>
        modalOpen({
          title: i18n.t('SESSIONS_KEY'),
          content: (
            <InstanceSessions
              environmentId={id}
              instanceId={item?.instance_uuid}
            />
          ),
          maxWidth: 'md',
        }),

      hidden: !permissions?.list_environment_instance_sessions,
    },
    {
      key: 'btn-alarm-logs',
      label: i18n.t('ALARMS_LOGS_KEY'),
      onClick: () =>
        modalOpen({
          title: i18n.t('ALARMS_LOGS_KEY'),
          subtitle: environmentSelected?.name,
          content: (
            <AlarmsAndLogs
              environmentId={id}
              instanceId={item?.instance_uuid}
              modalClose={modalClose}
            />
          ),
          maxWidth: 'md',
        }),
      hidden: isClientOperator(role),
    },
    {
      key: 'btn-reinitialize',
      label: i18n.t('REINITIALIZE_KEY'),
      onClick: () => handleRebootInstance(id, item),
      hidden: !permissions?.can_restart_environment_instances,
    },
    {
      key: 'btn-unregister-instances',
      label: i18n.t('UNREGISTER_INSTANCES'),
      onClick: () =>
        handleUnlinkInstance(item?.instance_uuid, item?.instance_id),
      hidden:
        (isPeriodOrManual && !isMulticloud) ||
        !permissions?.can_unregister_environment_instances,
    },
    {
      key: 'btn-unblock-instance',
      label: i18n.t('UNBLOCK_INSTANCE_KEY'),
      onClick: () =>
        handleUnblockInstance(item?.instance_uuid, item?.instance_id),
      hidden:
        item?.status !== 'BLOQUEADA' ||
        !permissions?.can_unblock_environment_instances,
    },
    {
      key: 'btn-block-instance',
      label: i18n.t('BLOCK_INSTANCE_KEY'),
      onClick: () =>
        handleBlockInstance(item?.instance_uuid, item?.instance_id),
      hidden:
        item?.status === 'BLOQUEADA' ||
        !permissions?.can_block_environment_instances,
    },
    {
      key: 'btn-terminate-instance',
      label: i18n.t('TERMINATE_INSTANCE_KEY'),
      onClick: () => handleTerminateInstance(item),
      hidden:
        isMulticloud ||
        (!isPeriodOrManual && !item?.is_spot) ||
        !permissions?.can_terminate_environment_instances,
    },
    {
      key: 'btn-simulate-app',
      label: i18n.t('TEST_OPEN_KEY'),
      onClick: () => handleSimulateApp(item),
      hidden: !permissions?.can_simulate_applications,
    },
    {
      key: 'btn-test-zero-trust',
      label: i18n.t('TEST_ZERO_TRUST_KEY'),
      onClick: () => handleTestZeroTrust(item),
      hidden: !permissions?.validate_instance_zero_trust || !canUseZeroTrust,
    },
  ];

  const connectAsItems = item?.connection_usernames?.map((username, index) => ({
    key: cyrb53Hash(username),
    id: `btn-connect-as-${item?.instance_id}-${index}`,
    label: `${i18n.t('CONNECT_LIKE_KEY')} ${username}`,
    onClick: () => handleConnect(item?.instance_id, username),
    hidden: !permissions?.can_connect_instance,
    style: {
      borderTop: index === 0 ? '1px solid #EEE' : '',
    },
  }));

  if (connectAsItems) {
    menuActionItems.push(...connectAsItems);
  }

  return menuActionItems.filter(action => !action.hidden);
};
